/* eslint-disable react/no-array-index-key */
/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-curly-newline */
import React, { FC, ReactElement, useState, useEffect, useRef } from 'react'
import { observer, inject } from 'mobx-react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import * as yup from 'yup'
import { Formik, FieldArray } from 'formik'
import { Form, Alert, Modal, Button as BootstrapButton } from 'react-bootstrap'
import { FcSettings } from 'react-icons/fc'
import _ from 'lodash'
import moment from 'moment'
import API from '@aws-amplify/api'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Editor } from '@tinymce/tinymce-react'

import { Tooltip, ListItem, ListItemText, Divider, Button } from '@material-ui/core'
import useWindowSize from '../../../../../utils/windowSize'

import { Template, Prospects as ProspectsStore } from '../../../../../stores/prospects'
import { Related as RelatedStore } from '../../../../../stores/related'
import { Settings as SettingsStore } from '../../../../../stores/settings'
import LoadingButton from '../../../../LoadingButton'
import { ToastTypeEnum } from '../../../Toasts'
import Spinner from '../../../../Spinner'
import DaysPopper from './DaysPopper'

interface Touch {
  sendOnReply: boolean | undefined
  thread: boolean | undefined
  disabled: boolean | false
  daysBetween: string | undefined
  subject: string
  body: string
}
interface Props extends RouteComponentProps {
  prospects?: ProspectsStore
  related?: RelatedStore
  settings?: SettingsStore
  maneuverType: string
  campaign: string
  templates?: Template[]
  daysBetween?: string[]
}

const tomorrow = moment()
  .add(1, 'day')
  .toDate()

const schema = yup.object({
  kickOffDate: yup
    .date()
    .min(new Date(), 'Kick off date must be in the future')
    .required('Kick off date is required'),
})

const TouchesForm: FC<Props> = ({
  maneuverType,
  campaign,
  prospects,
  related,
  settings,
  templates,
  daysBetween: days,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [currentType, setCurrentType] = useState(0)
  const [openDaysPopper, setOpenDaysPopper] = useState(false)
  const [openTodayModal, setOpenTodayModal] = useState(false)
  const [activateValues, setActivateValues] = useState<{
    touches: Touch[] | undefined
    kickOffDate: Date
    notifyOnComplete: boolean
  }>()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const height = useWindowSize()

  const prevOpen = React.useRef(openDaysPopper)
  useEffect(() => {
    if (prevOpen.current === true && openDaysPopper === false) {
      anchorRef?.current?.focus()
    }

    prevOpen.current = openDaysPopper
  }, [openDaysPopper])

  useEffect(() => {
    settings?.getSignatures()
  }, [])
  if (settings?.isLoading) {
    return <Spinner />
  }
  let emailBodyHeight = 150
  if (height > 825) {
    emailBodyHeight = 150 + height - 825
  }

  const handleTypeChange = (newValue: number): void => {
    setCurrentType(newValue)
    setOpenDaysPopper(false)
  }
  const activeMission = prospects?.prospect?.emailMetadata?.activeMission || []

  const touches = templates?.map((template, i) => {
    let touchDisabled = false
    if (campaign === 'Current Mission' && activeMission.length !== 0 && activeMission.length <= i) {
      touchDisabled = true
    }

    const emailOptionsSource = campaign === 'Current Mission' ? activeMission[i] : template
    return {
      ...template,
      daysBetween: _.get(days, i),
      sendOnReply: (emailOptionsSource && emailOptionsSource.sendOnReply) || false,
      thread: (emailOptionsSource && emailOptionsSource.thread) || false,
      disabled: (emailOptionsSource && emailOptionsSource.disabled) || touchDisabled,
    }
  })

  const handleToggleDaysPopper = (): void => {
    setOpenDaysPopper(prev => !prev)
  }

  const handleEditorChange = (
    event: {
      target: {
        getContent: () => string
      }
    },
    setFieldValue: (field: string, value: string) => void,
    field: string,
  ): void => {
    let str = event.target.getContent()
    str = str.replaceAll('<p class="p2">&nbsp;</p>\n', '')
    str = str.replaceAll('<p class="p1"><br /></p>\n', '')
    str = str.replaceAll('&nbsp;&nbsp;', '')
    str = str.replaceAll('<p class="p1"><br />&nbsp;&nbsp;</p>\n', '')
    str = str.replaceAll(
      '<p class="p2">&nbsp;</p>\n<p class="p2">&nbsp;</p>',
      '<p class="p2">&nbsp;</p>',
    )
    setFieldValue(field, str)
  }

  const activate = async (data?: {
    touches: Touch[] | undefined
    kickOffDate: Date
    notifyOnComplete: boolean
  }): Promise<void> => {
    setIsLoading(true)
    const values = !data ? activateValues : data
    let firstTouchTemplates = _.cloneDeep(prospects?.prospect?.firstTouchTemplates[campaign])
    let followUpTemplates = _.cloneDeep(prospects?.prospect?.followUpTemplates[campaign])
    let nurtureTemplates = _.cloneDeep(prospects?.prospect?.nurtureTemplates[campaign])

    let firstTouchDaysBetween = _.cloneDeep(prospects?.prospect?.firstTouchDaysBetween[campaign])
    let followUpDaysBetween = _.cloneDeep(prospects?.prospect?.followUpDaysBetween[campaign])
    let nurtureDaysBetween = _.cloneDeep(prospects?.prospect?.nurtureDaysBetween[campaign])

    const modifiedTemplates = values?.touches?.map((t, i) => ({
      // if this is a threaded email, set the subject value to the previous subject
      subject:
        i !== 0 && t.thread && touches && touches[i - 1].subject
          ? touches[i - 1].subject
          : t.subject,
      body: t.body,
      sendOnReply: t.sendOnReply,
      thread: t.thread,
      disabled: t.disabled,
    }))
    const daysBetween = values?.touches?.map(t => _.get(t, 'daysBetween', '0'))

    if (modifiedTemplates && daysBetween) {
      switch (maneuverType.toLowerCase()) {
        case 'first touch': {
          firstTouchTemplates = modifiedTemplates
          firstTouchDaysBetween = daysBetween
          break
        }
        case 'follow up': {
          followUpTemplates = modifiedTemplates
          followUpDaysBetween = daysBetween
          break
        }
        case 'nurture':
        default: {
          nurtureTemplates = modifiedTemplates
          nurtureDaysBetween = daysBetween
        }
      }
    }

    if (prospects?.prospect) {
      const { id, email } = prospects?.prospect

      const payload = {
        customFirstEmail: true,
        genericFirstEmail: false,
        engagementSequence: '',
        maneuver: maneuverType,
        leadId: id.toString(),
        activated: true,
        status: 'EMAIL Scheduled',
        firstTouchTemplates,
        followUpTemplates,
        nurtureTemplates,
        firstTouchDaysBetween,
        followUpDaysBetween,
        nurtureDaysBetween,
        sendDate: moment(values?.kickOffDate).format('MM/DD/YYYY'),
        notifyOnComplete: values?.notifyOnComplete,
        emailMetadata: prospects?.prospect?.emailMetadata || {},
        missionName: campaign,
      }
      const myInit = { body: payload }
      try {
        await API.put('mav-outlook-api', `/leads/${email}`, myInit)

        prospects?.setCacheValid(false)
        related?.setCacheValid(false)

        prospects?.getLead(email)
        setIsLoading(false)

        prospects?.pushToToasts({
          type: ToastTypeEnum.INFO,
          message: 'Lead updated',
        })
      } catch (e) {
        console.error(e)

        prospects?.setIsLoading(false)
        setIsLoading(false)
      }
    }
  }

  const hideTodayModal = (): void => {
    setOpenTodayModal(false)
  }

  const submitModal = (): void => {
    hideTodayModal()
    activate()
  }

  const addToMaverick = (values: {
    touches: Touch[] | undefined
    kickOffDate: Date
    notifyOnComplete: boolean
  }): void => {
    if (
      moment(values.kickOffDate).format('MM/DD/YYYY') === moment(new Date()).format('MM/DD/YYYY')
    ) {
      setOpenTodayModal(true)
      setActivateValues(values)
      return
    }

    activate(values)
  }

  const saveTouches = async (values: {
    touches: Touch[] | undefined
    kickOffDate: Date
    notifyOnComplete: boolean
  }): Promise<void> => {
    setIsLoading(true)

    let firstTouchTemplates = _.cloneDeep(prospects?.prospect?.firstTouchTemplates[campaign])
    let followUpTemplates = _.cloneDeep(prospects?.prospect?.followUpTemplates[campaign])
    let nurtureTemplates = _.cloneDeep(prospects?.prospect?.nurtureTemplates[campaign])

    let firstTouchDaysBetween = _.cloneDeep(prospects?.prospect?.firstTouchDaysBetween[campaign])
    let followUpDaysBetween = _.cloneDeep(prospects?.prospect?.followUpDaysBetween[campaign])
    let nurtureDaysBetween = _.cloneDeep(prospects?.prospect?.nurtureDaysBetween[campaign])
    const modifiedTemplates = values.touches?.map((t, i) => ({
      // if this is a threaded email, set the subject value to the previous subject
      subject:
        i !== 0 && t.thread && touches && touches[i - 1].subject
          ? touches[i - 1].subject
          : t.subject,
      body: t.body,
      sendOnReply: t.sendOnReply,
      thread: t.thread,
      disabled: t.disabled,
    }))
    const daysBetween = values.touches?.map(t => _.get(t, 'daysBetween', '0'))
    let maneuver = ''
    if (modifiedTemplates && daysBetween) {
      switch (maneuverType.toLowerCase()) {
        case 'first touch': {
          firstTouchTemplates = modifiedTemplates
          firstTouchDaysBetween = daysBetween
          maneuver = 'firstTouch'
          break
        }
        case 'follow up': {
          followUpTemplates = modifiedTemplates
          followUpDaysBetween = daysBetween
          maneuver = 'followUp'
          break
        }
        case 'nurture':
        default: {
          nurtureTemplates = modifiedTemplates
          nurtureDaysBetween = daysBetween
          maneuver = 'nurture'
        }
      }
    }

    if (prospects?.prospect) {
      const { id, email } = prospects?.prospect
      const payload = {
        leadId: id.toString(),
        followUpTemplates,
        maneuver,
        activated: false,
        nurtureTemplates,
        firstTouchTemplates,
        followUpDaysBetween,
        firstTouchDaysBetween,
        nurtureDaysBetween,
        notifyOnComplete: values.notifyOnComplete,
      }
      const myInit = { body: payload }
      try {
        await API.put('mav-outlook-api', `/leads/${email}`, myInit)

        prospects?.setCacheValid(false)
        related?.setCacheValid(false)
        setIsLoading(false)

        prospects?.pushToToasts({
          type: ToastTypeEnum.INFO,
          message: 'Leads saved',
        })
      } catch (e) {
        console.error(e)

        prospects?.setIsLoading(false)
        setIsLoading(false)
      }
    }
  }

  let tomorrowOrNextWeekday = moment(tomorrow)
  let initialValue
  const notifyOnComplete = prospects?.prospect?.notifyOnComplete || false
  if (prospects?.prospect && moment(prospects?.prospect?.customSendDate).isValid()) {
    initialValue = moment(prospects.prospect.customSendDate * 1000)
  }

  if (tomorrowOrNextWeekday.day() === 0) {
    // Custom send date is Sunday, add one day to make it Monday
    tomorrowOrNextWeekday = tomorrowOrNextWeekday.add(1, 'day')
  } else if (tomorrowOrNextWeekday.day() === 6) {
    // Custom send date is Saturday, add two days to make it Monday
    tomorrowOrNextWeekday = tomorrowOrNextWeekday.add(2, 'days')
  }

  const isWeekday = (date: Date): boolean => {
    const day = date.getDay()
    return day !== 0 && day !== 6
  }
  return (
    <>
      <Formik
        validationSchema={schema}
        onSubmit={addToMaverick}
        initialValues={{
          touches,
          kickOffDate: initialValue || tomorrowOrNextWeekday.toDate(),
          notifyOnComplete,
        }}
        enableReinitialize
      >
        {({
          handleSubmit,
          values,
          handleChange,
          touched,
          errors,
          submitCount,
          setFieldValue,
        }): ReactElement => {
          return (
            <Form noValidate onSubmit={handleSubmit}>
              <FieldArray name="touches">
                {(): ReactElement => {
                  return (
                    <>
                      <div className="modal_section">
                        {templates?.map((template, i) => (
                          <ListItem
                            button
                            className={`mav-modal-title ${currentType === i ? 'active' : ''}`}
                            key={`${i} + 1`}
                            onClick={(): void => handleTypeChange(i)}
                          >
                            <ListItemText primary={`EMAIL #${i + 1}`} />
                          </ListItem>
                        ))}
                      </div>
                      <Divider />
                      {templates?.map((template, i) => {
                        const daysBetween = {
                          value: _.get(values, `touches.${i}.daysBetween`),
                          touched: _.get(touched, `touches.${i}.daysBetween`),
                          errors: _.get(errors, `touches.${i}.daysBetween`),
                        }
                        const sendOnReply = {
                          value: _.get(values, `touches.${i}.sendOnReply`),
                          touched: _.get(touched, `touches.${i}.sendOnReply`),
                        }
                        const thread = {
                          value: _.get(values, `touches.${i}.thread`),
                          touched: _.get(touched, `touches.${i}.thread`),
                        }
                        const disabled = {
                          value: _.get(values, `touches.${i}.disabled`),
                          touched: _.get(values, `touches.${i}.disabled`),
                        }

                        const subject = {
                          value: _.get(values, `touches.${i}.subject`),
                          touched: _.get(touched, `touches.${i}.subject`),
                          errors: _.get(errors, `touches.${i}.subject`),
                        }

                        const body = {
                          value: _.get(values, `touches.${i}.body`),
                          touched: _.get(touched, `touches.${i}.body`),
                          errors: _.get(errors, `touches.${i}.body`),
                        }
                        const leadInfo = [] as object[]
                        if (prospects?.prospect?.firstName) {
                          leadInfo.push({
                            value: prospects?.prospect?.firstName,
                            label: 'First Name',
                          })
                        }
                        if (prospects?.prospect?.lastName) {
                          leadInfo.push({
                            value: prospects?.prospect?.lastName,
                            label: 'Last Name',
                          })
                        }
                        if (prospects?.prospect?.company) {
                          leadInfo.push({
                            value: prospects?.prospect?.company,
                            label: 'Company Name',
                          })
                        }
                        if (prospects?.prospect?.title) {
                          leadInfo.push({
                            value: prospects?.prospect?.title,
                            label: 'Title',
                          })
                        }
                        if (prospects?.prospect?.industry) {
                          leadInfo.push({
                            value: prospects?.prospect?.industry,
                            label: 'Industry',
                          })
                        }
                        if (
                          settings?.signatures &&
                          settings?.signatures[0] &&
                          settings.signatures[0].id !== 'null'
                        ) {
                          leadInfo.push({
                            value: '{Signature}',
                            label: 'Signature',
                          })
                        }
                        if (currentType === i) {
                          const bodyValue = body.value.replace(
                            '{Signature}',
                            settings?.signatures[0]?.signature,
                          )
                          return (
                            <div className="email_section">
                              <div style={{ flex: 3 }}>
                                <div className="email-first-line">
                                  {i !== 0 && (
                                    <Button
                                      ref={anchorRef}
                                      aria-controls={openDaysPopper ? 'days-popper' : undefined}
                                      aria-haspopup="true"
                                      className="trash"
                                      onClick={handleToggleDaysPopper}
                                    >
                                      <FcSettings
                                        className={`${openDaysPopper ? 'active-delete' : ''}`}
                                      />
                                    </Button>
                                  )}
                                  {i !== 0 && (
                                    <DaysPopper
                                      openDaysPopper={openDaysPopper}
                                      anchorRef={anchorRef}
                                      setOpenDaysPopper={setOpenDaysPopper}
                                    >
                                      <Form.Group
                                        controlId={`days-after-touch-${i}`}
                                        className="days-after"
                                      >
                                        <Form.Label>{`Days after touch ${i}?`}</Form.Label>
                                        <Form.Control
                                          type="number"
                                          min={1}
                                          name={`touches.${i}.daysBetween`}
                                          required
                                          value={daysBetween.value}
                                          onChange={handleChange}
                                          isInvalid={submitCount > 0 && !!daysBetween.errors}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          <Alert variant="danger">{daysBetween.errors}</Alert>
                                        </Form.Control.Feedback>
                                      </Form.Group>
                                      <div className="checkboxs">
                                        <Form.Group controlId={`touches.${i}.disabled`}>
                                          <Form.Check
                                            type="checkbox"
                                            id="disabled-checkbox"
                                            className="email-meta-checkbox"
                                            name={`touches.${i}.disabled`}
                                            label="Disable this touch. Disabling one touch will disable all touches after it."
                                            checked={disabled.value}
                                            onChange={(): void => {
                                              const touchesLength = touches?.length || 0
                                              let canFlip = true
                                              let downCount = i
                                              do {
                                                downCount -= 1
                                                if (
                                                  i !== 1 &&
                                                  _.get(values, `touches.${downCount}.disabled`)
                                                ) {
                                                  canFlip = false
                                                }
                                              } while (downCount > 1)
                                              if (canFlip) {
                                                setFieldValue(
                                                  `touches.${i}.disabled`,
                                                  !disabled.value,
                                                )
                                                if (i < touchesLength && !disabled.value) {
                                                  let tmp = i
                                                  do {
                                                    tmp += 1
                                                    setFieldValue(
                                                      `touches.${tmp}.disabled`,
                                                      !disabled.value,
                                                    )
                                                  } while (tmp < touchesLength)
                                                }
                                              } else {
                                                prospects?.pushToToasts({
                                                  type: ToastTypeEnum.ERROR,
                                                  message:
                                                    'You cannot enable this touch without enabling all prior touches',
                                                })
                                              }
                                            }}
                                          />
                                        </Form.Group>
                                        <Form.Group controlId={`touches.${i}.sendOnReply`}>
                                          <Form.Check
                                            type="checkbox"
                                            id="send-on-reply-checkbox"
                                            className="email-meta-checkbox"
                                            name={`touches.${i}.sendOnReply`}
                                            label="Send on Reply"
                                            checked={sendOnReply.value}
                                            onChange={handleChange}
                                          />
                                        </Form.Group>
                                        <Form.Group controlId={`touches.${i}.thread`}>
                                          <Form.Check
                                            type="checkbox"
                                            id="thread-checkbox"
                                            className="email-meta-checkbox"
                                            name={`touches.${i}.thread`}
                                            label="Thread this touch to previous touch"
                                            checked={thread.value}
                                            onChange={handleChange}
                                          />
                                        </Form.Group>
                                      </div>
                                    </DaysPopper>
                                  )}
                                  <Tooltip
                                    title={
                                      thread.value
                                        ? 'The previous email subject will be used for threaded emails'
                                        : ' '
                                    }
                                    disableFocusListener={!thread.value}
                                    disableTouchListener={!thread.value}
                                    disableHoverListener={!thread.value}
                                  >
                                    <Form.Group
                                      controlId={`email-subject-touch-${i}`}
                                      className="email-subject"
                                    >
                                      <Form.Label>Email Subject</Form.Label>
                                      <Form.Control
                                        type="text"
                                        name={`touches.${i}.subject`}
                                        required
                                        value={subject.value}
                                        onChange={handleChange}
                                        isValid={subject.touched && !subject.errors}
                                        isInvalid={submitCount > 0 && !!subject.errors}
                                        disabled={thread.value && i !== 0}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        <Alert variant="danger">{subject.errors}</Alert>
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </Tooltip>
                                </div>
                                <Form.Group
                                  controlId={`email-body-touch-${i}`}
                                  className="email-body-group"
                                >
                                  <Form.Label>Email Body</Form.Label>
                                  <Editor
                                    textareaName={`touches.${i}.body`}
                                    // className="email-body-textarea"
                                    apiKey="dc71buijbp92lbdveqijzgigj9gxfosqm6li1pr7o1s20vds"
                                    value={bodyValue}
                                    id={`email-body-touch-${i}`}
                                    init={{
                                      browser_spellcheck: true,
                                      branding: false,
                                      br_in_pre: false,
                                      height: emailBodyHeight,
                                      menu: {
                                        custom: {
                                          title: 'Custom Menu',
                                          items: 'undo redo myCustomMenuItem',
                                        },
                                      },
                                      menubar: false,
                                      plugins: [
                                        'advlist autolink lists link',
                                        'searchreplace visualblocks',
                                        'paste wordcount contextmenu',
                                      ],
                                      contextmenu: 'paste link ',
                                      setup: (editor): void => {
                                        editor.ui.registry.addMenuButton('menuDateButton', {
                                          text: 'Variables',
                                          // toolbar_sticky: false,
                                          fetch(callback) {
                                            const itemsArr: {
                                              type: 'togglemenuitem' | undefined
                                              text: string
                                              onAction: () => void
                                            }[] = [
                                              {
                                                type: 'togglemenuitem',
                                                text: 'First Name',
                                                onAction: (): void => {
                                                  editor.insertContent('{FirstName}')
                                                },
                                              },
                                              {
                                                type: 'togglemenuitem',
                                                text: 'Last Name',
                                                onAction: (): void => {
                                                  editor.insertContent('{LastName}')
                                                },
                                              },
                                              {
                                                type: 'togglemenuitem',
                                                text: 'Company Name',
                                                onAction: (): void => {
                                                  editor.insertContent('{CompanyName}')
                                                },
                                              },
                                              {
                                                type: 'togglemenuitem',
                                                text: 'Title',
                                                onAction: (): void => {
                                                  editor.insertContent('{Title}')
                                                },
                                              },
                                              {
                                                type: 'togglemenuitem',
                                                text: 'Industry',
                                                onAction: (): void => {
                                                  editor.insertContent('{Industry}')
                                                },
                                              },
                                              {
                                                type: 'togglemenuitem',
                                                text: 'Opt Out Message',
                                                onAction: (): void => {
                                                  editor.setContent(
                                                    `${editor.getContent()} <p>To stop receiving emails similar to this one, reply "Unsubscribe" </p>`,
                                                    { format: 'html' },
                                                  )
                                                },
                                              },
                                            ]
                                            if (
                                              settings?.signatures &&
                                              settings?.signatures[0] &&
                                              settings?.signatures[0].id !== 'null'
                                            ) {
                                              itemsArr.push({
                                                type: 'togglemenuitem',
                                                text: 'Signature',
                                                onAction: (): void => {
                                                  editor.insertContent('{Signature}')
                                                },
                                              })
                                            }
                                            callback(itemsArr)
                                          },
                                        })
                                      },
                                      toolbar:
                                        'menuDateButton | fontselect | bold italic bullist numlist outdent indent',
                                    }}
                                    onEditorChange={(value): void => {
                                      setFieldValue(`touches.${i}.body`, value)
                                    }}
                                    onChange={(value): void => {
                                      handleEditorChange(value, setFieldValue, `touches.${i}.body`)
                                    }}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    <Alert variant="danger">{body.errors}</Alert>
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </div>
                              <div style={{ flex: 1 }}>
                                <Form.Group controlId="bottom-part" className="px-3">
                                  <Form.Group
                                    controlId="kick-off-date"
                                    className="mt-3 kick_data_container"
                                  >
                                    <Form.Label>Kick Off Date</Form.Label>
                                    <Form.Control
                                      as={DatePicker}
                                      name="kickOffDate"
                                      minDate={new Date()}
                                      required
                                      value={moment(values.kickOffDate).format('MM/DD/YYYY')}
                                      isValid={touched.kickOffDate && !errors.kickOffDate}
                                      isInvalid={submitCount > 0 && !!errors.kickOffDate}
                                      filterDate={isWeekday}
                                      onSelect={(value: Date): void => {
                                        value.setHours(23, 59, 59)
                                        setFieldValue('kickOffDate', value)
                                      }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      <Alert variant="danger">{errors.kickOffDate}</Alert>
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                  <Form.Group controlId="notifyOnComplete">
                                    <Form.Check
                                      type="checkbox"
                                      id="notify-checkbox"
                                      className="notify-checkbox"
                                      label="Notify When Mission Completes"
                                      checked={values.notifyOnComplete}
                                      onChange={(): void =>
                                        setFieldValue('notifyOnComplete', !values.notifyOnComplete)
                                      }
                                    />
                                  </Form.Group>
                                  <Form.Group controlId="save">
                                    <LoadingButton
                                      variant="primary"
                                      isLoading={isLoading}
                                      block
                                      onClick={(): void => {
                                        if (_.entries(errors.touches).length > 0) {
                                          prospects?.pushToToasts({
                                            type: ToastTypeEnum.ERROR,
                                            message:
                                              'Errors in template. Please go back and correct any errors before continuing',
                                          })
                                        } else {
                                          saveTouches(values)
                                        }
                                      }}
                                    >
                                      Save Email
                                    </LoadingButton>
                                  </Form.Group>
                                  <Form.Group controlId="actions">
                                    <LoadingButton
                                      variant="primary"
                                      isLoading={isLoading}
                                      type="submit"
                                      block
                                      onClick={(): void => {
                                        if (_.entries(errors).length > 0) {
                                          prospects?.pushToToasts({
                                            type: ToastTypeEnum.ERROR,
                                            message:
                                              'Errors in template. Please go back and correct any errors before continuing',
                                          })
                                        }
                                      }}
                                    >
                                      Activate Maverick
                                    </LoadingButton>
                                  </Form.Group>
                                </Form.Group>
                              </div>
                            </div>
                          )
                        }
                        return <></>
                      })}
                    </>
                  )
                }}
              </FieldArray>
            </Form>
          )
        }}
      </Formik>
      <Modal show={openTodayModal} onHide={(): void => hideTodayModal()}>
        <Modal.Body>
          <p>Are you sure you want to start your outbound mission Today?</p>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={(): void => hideTodayModal()}>
            No
          </BootstrapButton>
          <BootstrapButton variant="primary" onClick={(): void => submitModal()}>
            Yes
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default inject('prospects', 'related', 'settings')(withRouter(observer(TouchesForm)))
